import { useState, useEffect } from 'react';
import { axios } from '../App';
import { toast } from 'sonner';
import { Clock, AlertTriangle, Target, Brain, Users } from 'lucide-react';

const eventIcons = {
  threat: AlertTriangle,
  mission: Target,
  intelligence: Brain,
  asset: Users,
  user: Users
};

const eventColors = {
  threat: 'text-red-500',
  mission: 'text-sky-500',
  intelligence: 'text-emerald-500',
  asset: 'text-amber-500',
  user: 'text-zinc-500'
};

export default function Timeline() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTimeline();
    const interval = setInterval(loadTimeline, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadTimeline = async () => {
    try {
      const { data } = await axios.get('/timeline');
      setEvents(data);
    } catch (error) {
      toast.error('Failed to load timeline');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-sky-500 font-mono text-sm">LOADING EVENT TIMELINE...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="timeline-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight uppercase">Event Timeline</h1>
        <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1 font-mono">
          Chronological operational log
        </p>
      </div>

      <div className="relative">
        <div className="absolute left-8 top-0 bottom-0 w-px bg-zinc-800"></div>

        <div className="space-y-6">
          {events.length === 0 ? (
            <div className="text-center py-8 text-zinc-600 font-mono text-sm">
              No events recorded
            </div>
          ) : (
            events.map((event, index) => {
              const Icon = eventIcons[event.event_type] || Clock;
              const colorClass = eventColors[event.event_type] || 'text-zinc-500';

              return (
                <div key={event.id} className="relative flex gap-6" data-testid={`timeline-event-${event.id}`}>
                  <div className="relative z-10 flex-shrink-0">
                    <div
                      className={`w-16 h-16 bg-zinc-950 border-2 border-zinc-800 rounded-sm flex items-center justify-center ${colorClass}`}
                    >
                      <Icon size={24} />
                    </div>
                  </div>

                  <div className="flex-1 bg-zinc-950/80 border border-zinc-800 rounded-sm p-6 hover:border-zinc-700 transition-colors">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="text-lg font-bold uppercase tracking-tight">
                          {event.title}
                        </h3>
                        <span
                          className={`text-xs font-mono uppercase px-2 py-1 rounded-sm bg-zinc-900 border border-zinc-800 inline-block mt-2 ${colorClass}`}
                        >
                          {event.event_type}
                        </span>
                      </div>
                      <span className="text-xs font-mono text-zinc-600">
                        {new Date(event.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-zinc-400 mt-3">{event.description}</p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}